import styled, {createGlobalStyle, css, keyframes} from "styled-components";
import {fontFamilies, brand} from "theme/tokens";
import {LogoMark} from "nav/BrandMark";
import {Brand, Topbar} from "nav/normal/NavBar.styles";
import {campaignPalette, halloweenTile} from "./campaignPalette";
import {CAMPAIGN_DISSOLVE_MS, isHalloweenEdition, type CampaignId} from "./campaignPlayback";

const fadeOut = keyframes`from {opacity: 1;} to {opacity: 0;}`;
const fadeIn = keyframes`from {opacity: 0;} to {opacity: 1;}`;
const sceneColors = css<{$scene: CampaignId}>`
	${({theme, $scene}) => {
		const p = campaignPalette(theme.tokens, $scene);
		return css`--campaign-ground: ${p.ground}; --campaign-ink: ${p.ink}; --campaign-muted: ${p.muted}; --campaign-action: ${p.action}; --campaign-action-ink: ${p.actionInk}; --campaign-arrow: ${p.arrow}; --campaign-arrow-ink: ${p.arrowInk};`;
	}}
`;
/**
 * Recolors the navigation brand for the active scene. The bar lives outside the landing tree, so this
 * is a global style, and it must stay scoped to the bar: the prerendered light-mode ink otherwise
 * reaches every other brand instance, including the provider-failure page that renders without the
 * app's own theme (three dark-mode contrast failures in the served-build suite).
 */
export const CampaignBrandStyle = createGlobalStyle<{$scene: CampaignId; $active: boolean; $gold: boolean; $halloween: boolean}>`
	${({theme, $scene, $active, $gold}) => $active && css`
		${Topbar} ${Brand} {
			position: relative;
			isolation: isolate;
			color: ${$gold ? brand.goldInk : campaignPalette(theme.tokens, $scene).ink};
			${$gold && css`&::before {content: ""; position: absolute; inset: -6px -8px; border-radius: 12px; background: ${brand.gold}; z-index: -1;}`}
		}
	`}
	/*
	 * The tile of the Halloween days (kitecorp/transferify-ui#3776, owner's decision D6), in the colour of the slide
	 * that plays, slime green for the pumpkin night: the bar's mark, and the consent gate's inside the landing
	 * surface, which is the same LogoMark at another size. Only while the landing is the page and its slide is a
	 * Halloween one; blue everywhere else, and blue again after the days. Two classes deep on purpose, so the rule
	 * outranks LogoMark's own single-class fill whichever sheet came last.
	 */
	${({$active, $halloween, $scene}) => $active && $halloween && isHalloweenEdition($scene) && css`
		${Topbar} ${Brand} ${LogoMark}, [data-campaign-surface="landing"] ${LogoMark} { background: ${halloweenTile($scene).tile}; color: ${halloweenTile($scene).tileInk}; }
	`}
`;

/** Inert allocation preserves the original composer position; no previous heading or copy is kept. */
export const CampaignSlot = styled.div`
	position: relative;
	flex: 1 1 0;
	max-width: 430px;
	min-width: 0;
	pointer-events: none;
	align-self: stretch;
	/* The stable outer band prevents initial assignment and dissolves from moving the page. */
	min-height: 0;

	@media (max-width: 900px) {
		max-width: 100%;
		min-height: 0;
		/* The gap the design asks for, stated as its own property so useCampaignSpace can read what was
		   DESIGNED rather than what it last applied - a reading taken off margin-block-start below
		   would be its own previous output and the two would chase each other (ui#2631).
		   (No backticks in a comment inside a styled-components literal - one would end the literal.) */
		--campaign-slot-design-gap: ${({theme}) => theme.tokens.mode === "dark" ? 0 : 20}px;
		/* On a surface with no room for a promotion this is space above nothing, and the page it comes
		   out of is the single screen the legal footer row already shortened, so campaignReserve hands
		   back whatever of it will not fit. The design value is the fallback before the first measure. */
		margin-block-start: var(--campaign-slot-gap, var(--campaign-slot-design-gap));
	}
`;
export const AllocationContainer = styled.div`container-type: inline-size;`;
export const Allocation = styled.div`
	pointer-events: none;
	max-height: var(--campaign-reserve-cap, none);
	@media (max-width: 900px) {
		/* Two display lines, three body lines and one secondary line at the original type metrics.
		   The narrow column needs a fourth body line. These empty boxes reserve space, not content. */
		--allocation-display: ${({theme}) => theme.tokens.mode === "dark" ? "clamp(46px, 5.7vw, 74px)" : "clamp(32px, 5.7vw, 74px)"};
		height: calc(2 * var(--allocation-display) + 40px + 75.328125px + 20.921875px);
		@container (max-width: 385px) { height: calc(2 * var(--allocation-display) + 40px + 100.4375px + 20.921875px); }
		@container (max-width: 310px) { height: calc(2 * var(--allocation-display) + 40px + 100.4375px + 41.84375px); }
	}
`;
/** Actual message + CTA bounds, separate from the inert layout allocation. */
export const CampaignReading = styled.div`
	position: absolute;
	inset: 0;
	display: grid;
	align-items: center;
	grid-template-rows: minmax(0, 1fr);
	@media (max-width: 900px) { inset: 0 0 auto; align-items: start; }
	&[data-mobile-space] {
		position: fixed;
		inset: auto;
		top: var(--campaign-space-top);
		left: var(--campaign-space-left);
		width: var(--campaign-space-width);
		height: var(--campaign-space-height);
		container: campaign-space / size;
		align-items: center;
	}

`;
export const CampaignCopy = styled.div<{$scene: CampaignId; $outgoing?: boolean; $dissolving: boolean; $paused: boolean}>`
	${sceneColors}
	grid-area: 1 / 1;
	color: var(--campaign-ink);
	pointer-events: none;
	${({$dissolving, $outgoing}) => $dissolving && css`animation: ${$outgoing ? fadeOut : fadeIn} ${CAMPAIGN_DISSOLVE_MS}ms ease-in-out both;`}
	animation-play-state: ${({$paused}) => $paused ? "paused" : "running"};
	@media (prefers-reduced-motion: reduce) { animation: none; }
	&[data-edition="newspaper"] { border-top: 3px double currentColor; padding-top: 12px; }
	&:is([data-edition="showcase"], [data-edition="kinetic"], [data-edition="cutpaper"], [data-edition="collage"]) {
		@media (min-width: 901px) { max-width: clamp(260px, 24vw, 360px); }
	}
`;
export const PlanLabel = styled.p`
	margin: 0 0 20px;
	font: 500 13px/1.4 ${fontFamilies.sans};
	letter-spacing: .01em;
	[data-edition="newspaper"] & { font: 400 24px/1.1 ${fontFamilies.displaySerif}; padding-bottom: 12px; border-bottom: 1px solid currentColor; }
	[data-edition="cover"] & { text-transform: uppercase; letter-spacing: .09em; font-size: 11px; }
`;
export const CampaignTitle = styled.h1`
	margin: 0;
	font-family: ${fontFamilies.displaySerif};
	font-weight: 400;
	font-size: var(--campaign-title-size, clamp(44px, 5.7vw, 82px));
	line-height: .96;
	letter-spacing: -.04em;
	white-space: pre-line;
	overflow-wrap: anywhere;
	[data-edition="cartoon"] & {
		font-family: ${fontFamilies.sans}; font-weight: 700; font-size: var(--campaign-title-size, clamp(38px, 4.5vw, 64px)); letter-spacing: -.055em;
	}
	[data-edition="cover"] & { font-style: italic; }
	[data-edition="ink"] & { font-size: var(--campaign-title-size, clamp(70px, 8.2vw, 138px)); line-height: .88; letter-spacing: -.045em; }
	[data-edition="showcase"] & { font-family: ${fontFamilies.sans}; font-weight: 500; font-size: var(--campaign-title-size, clamp(54px, 5.1vw, 86px)); line-height: 1.02; letter-spacing: -.065em; }
	[data-edition="kinetic"] & { font-family: ${fontFamilies.sans}; font-weight: 700; font-size: var(--campaign-title-size, clamp(60px, 5.2vw, 82px)); line-height: .95; letter-spacing: -.07em; }
	[data-edition="cutpaper"] & { font-size: var(--campaign-title-size, clamp(68px, 6vw, 90px)); line-height: .93; letter-spacing: -.045em; }
	[data-edition="collage"] & { font-size: var(--campaign-title-size, clamp(62px, 5.4vw, 84px)); letter-spacing: -.045em; }
	@media (max-width: 900px) { font-size: var(--campaign-title-size, clamp(44px, 8vw, 64px)); }
	@media (max-width: 430px) { font-size: var(--campaign-title-size, 44px); }
`;
export const CampaignLede = styled.p`
	margin: 22px 0 0;
	color: var(--campaign-muted);
	font: 400 15px/1.6 ${fontFamilies.sans};
	max-width: 390px;
	[data-edition="newspaper"] & { font: 400 22px/1.3 ${fontFamilies.displaySerif}; }
`;
export const CampaignCta = styled.button`
	pointer-events: auto;
	display: inline-flex;
	align-items: center;
	justify-content: space-between;
	gap: 24px;
	min-height: 48px;
	margin: 24px 0 0;
	/* Keep the tile inside the original control height, including the newspaper's longer reading block. */
	padding: 0 2px 0 20px;
	border: 1px solid var(--campaign-action);
	border-radius: 100px;
	background: var(--campaign-action);
	color: var(--campaign-action-ink);
	font: 600 14px/1.4 ${fontFamilies.sans};
	cursor: pointer;
	&:hover { filter: brightness(.95); }
	&:focus-visible { outline: 2px solid var(--campaign-ink); outline-offset: 4px; }
	[data-edition="newspaper"] & { border-radius: 3px; }
	[data-edition="cartoon"] & { border-radius: 14px; box-shadow: 3px 3px 0 var(--campaign-ink); }
	[data-edition="cover"] & { color: var(--campaign-ink); background: transparent; }
	[data-edition="showcase"] & { border-radius: 14px; }
	[data-edition="kinetic"] &, [data-edition="collage"] & { border-radius: 4px; }
`;
export const CampaignCtaArrow = styled.span`
	display: grid;
	place-items: center;
	flex: none;
	width: 43px;
	height: 43px;
	border-radius: 50%;
	background: var(--campaign-arrow);
	color: var(--campaign-arrow-ink);
	transition: transform .2s;
	[data-edition="newspaper"] & { border-radius: 0; background: transparent; }
	[data-edition="kinetic"] &, [data-edition="collage"] & { border-radius: 2px; }
	[data-edition="showcase"] & { border-radius: 9px; }
	[data-edition="cartoon"] & { border-radius: 10px; }
	${CampaignCta}:hover & { transform: rotate(-35deg); }
	@media (prefers-reduced-motion: reduce) { transition: none; }
	/* The compact free-space strip retains the existing 44px control height. */
	@container campaign-space (max-height: 20rem) { width: 26px; height: 26px; }
`;
export const CampaignNote = styled.p`
	margin: 14px 0 0;
	font: 400 12px/1.5 ${fontFamilies.sans};
	color: var(--campaign-muted);
`;
export const PrintColumns = styled.div`
	display: grid;
	grid-template-columns: repeat(2, minmax(0, 1fr));
	gap: 16px;
	margin-top: 18px;
	padding-top: 14px;
	border-top: 1px solid currentColor;
	p { margin: 6px 0 0; font: 400 17px/1.3 ${fontFamilies.displaySerif}; }
	strong { font: 600 12px/1.4 ${fontFamilies.sans}; }
	> div + div { padding-left: 16px; border-left: 1px solid currentColor; }
`;
export const ArtworkLayer = styled.div<{$scene: CampaignId}>`
	${sceneColors}
	position: absolute;
	inset: 0;
	z-index: 0;
	background: var(--campaign-ground);
	pointer-events: auto;
	overflow: hidden;
`;
export const ArtCanvas = styled.canvas<{$outgoing: boolean; $paused: boolean}>`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	pointer-events: none;
	${({$outgoing}) => $outgoing && css`animation: ${fadeOut} ${CAMPAIGN_DISSOLVE_MS}ms ease-in-out both;`}
	animation-play-state: ${({$paused}) => $paused ? "paused" : "running"};
	@media (prefers-reduced-motion: reduce) { animation: none; }
`;
export const ArtHitArea = styled.div`
	position: absolute;
	inset: 0;
	width: 100%;
	height: 100%;
	border: 0;
	padding: 0;
	background: transparent;
	cursor: pointer;
`;
const badgeTurn = keyframes`from {transform: rotate(-35deg);} to {transform: rotate(325deg);}`;
const badgeBounceA = keyframes`0%, 100% {scale: 1;} 50% {scale: 1.06;}`;
// Distinct names restart the bounce on consecutive reversals without a forced layout read.
const badgeBounceB = keyframes`from {scale: 1;} 50% {scale: 1.06;} to {scale: 1;}`;
export const OpenBadge = styled.span<{$scene: CampaignId}>`
	${sceneColors}
	${({theme, $scene}) => {
		const p = campaignPalette(theme.tokens, $scene);
		return css`--badge-dark: ${p.shadow}; --badge-white: ${p.white}; --badge-paper: ${theme.tokens.paper}; --badge-gold: ${p.gold}; --badge-gold-ink: ${brand.goldInk}; --badge-line: ${theme.tokens.line};`;
	}}
	/* Actual hit testing, not playback pause: a modal or protected control takes hover away. */
	[data-campaign-art]:not(:hover) + &, &[hidden] { display: none; }
	position: fixed;
	left: 0;
	top: 0;
	pointer-events: none;
	z-index: 2;
	will-change: transform;
	--badge-angle: 0deg;
	.badge-face {
		position: relative; isolation: isolate; display: flex; gap: 9px; align-items: center; justify-content: center;
		min-width: 77px; height: 42px; padding: 0 15px; border-radius: 10px 28px 28px 28px;
		background: var(--badge-dark); color: var(--badge-white); font: 600 12px/1 ${fontFamilies.sans}; white-space: nowrap;
		box-shadow: 0 16px 55px color-mix(in srgb, var(--badge-dark) 12%, transparent), 0 2px 5px color-mix(in srgb, var(--badge-dark) 4%, transparent);
	}
	.badge-arrow { width: 15px; height: 15px; flex: none; transform: rotate(-35deg); }
	.badge-hole, .badge-dots, .badge-string { display: none; }
	&[data-shape="gold"] {
		.badge-face { background: var(--badge-gold); color: var(--badge-gold-ink); border-radius: 50%; width: 80px; min-width: 80px; height: 80px; white-space: normal; text-align: center; flex-direction: column; gap: 9px; padding: 0 10px; }
		.badge-arrow { animation: ${badgeTurn} 6s linear infinite; }
	}
	&[data-shape="stamp"] {
		.badge-face { width: 80px; min-width: 80px; min-height: 80px; height: auto; white-space: normal; text-align: center; padding: 8px; border-radius: 0; border: 1px solid var(--campaign-ink); background: var(--campaign-ground); color: var(--campaign-ink); box-shadow: none; font: small-caps 400 21px/1 ${fontFamilies.displaySerif}; transform: rotate(-6deg); }
		.badge-word { text-transform: uppercase; }
	}
	&:is([data-shape="stamp"], [data-shape="word"], [data-shape="blob"], [data-shape="layered"], [data-shape="tab"]) .badge-arrow { display: none; }
	&[data-shape="word"] .badge-face { min-width: 0; height: auto; padding: 5px; border-radius: 0; background: transparent; color: var(--campaign-ink); box-shadow: none; font: italic 400 32px/1.1 ${fontFamilies.displaySerif}; }
	&[data-shape="blob"] .badge-face { width: 86px; min-width: 86px; height: 66px; white-space: normal; text-align: center; border-radius: 40% 60% 55% 45%; border: 3px solid var(--badge-gold-ink); background: var(--badge-gold); color: var(--badge-gold-ink); box-shadow: 3px 3px 0 var(--badge-gold-ink); font-size: 15px; }
	&[data-shape="blob"][data-bounce="a"] .badge-face { animation: ${badgeBounceA} 220ms ease-out; }
	&[data-shape="blob"][data-bounce="b"] .badge-face { animation: ${badgeBounceB} 220ms ease-out; }
	&[data-shape="chip"] .badge-face { border-radius: 8px; background: var(--badge-paper); color: var(--campaign-ink); border: 1px solid var(--badge-line); box-shadow: 0 5px 12px color-mix(in srgb, var(--campaign-ink) 18%, transparent), 0 1px 2px color-mix(in srgb, var(--campaign-ink) 12%, transparent); }
	&[data-shape="tag"] {
		.badge-face { padding: 0 24px 0 17px; border-radius: 0; background: transparent; color: var(--badge-gold-ink); box-shadow: none; transform: rotate(var(--badge-angle)); transform-origin: 0 0; }
		.badge-face::before { content: ""; position: absolute; inset: 0; z-index: -1; background: var(--badge-gold); clip-path: polygon(0 0, calc(100% - 12px) 0, 100% 50%, calc(100% - 12px) 100%, 0 100%); }
		.badge-hole { display: block; position: absolute; left: -3px; top: -3px; width: 6px; height: 6px; background: var(--badge-gold-ink); border: 1px solid var(--badge-gold); border-radius: 50%; }
		.badge-string { display: block; position: absolute; left: 0; top: 0; width: var(--string-length); height: 1px; background: var(--campaign-ink); transform-origin: 0 0; transform: rotate(var(--string-angle)); }
	}
	&[data-shape="layered"] .badge-face { width: 80px; min-width: 80px; height: 80px; white-space: normal; text-align: center; padding: 0 10px; border-radius: 50%; background: var(--badge-paper); color: var(--campaign-ink); border: 1px solid var(--campaign-ink); box-shadow: 3px 3px 0 var(--badge-gold), 3px 3px 0 1px var(--campaign-ink); }
	&[data-shape="tab"] {
		.badge-face { flex-direction: column; align-items: flex-start; gap: 8px; height: 55px; padding: 10px 15px; border-radius: 12px 12px 0 0; border: 1px solid var(--campaign-ink); background: var(--badge-paper); color: var(--campaign-ink); }
		.badge-dots { display: flex; gap: 3px; }
	}
	.badge-dots i { width: 3px; height: 3px; border-radius: 50%; background: currentColor; }
	@media (hover: none), (pointer: coarse) { display: none; }
	@media (prefers-reduced-motion: reduce) {
		&& .badge-face, && .badge-arrow { animation: none; transform: none; scale: 1; }
	}
`;

/** Content priority is responsive to the actual remaining space, not to the size of the uploader. */
export const CampaignFitStyle = createGlobalStyle<{}>`
	@container campaign-space (max-height: 32rem) {
		${CampaignReading} ${CampaignCopy} ${CampaignNote} { display: none; }
	}
	@container campaign-space (max-height: 26rem) {
		${CampaignReading} ${CampaignCopy} ${PrintColumns} { display: none; }
		${CampaignReading} ${CampaignCopy} { --campaign-title-size: clamp(28px, 10cqw, 42px); }
	}
	@container campaign-space (max-height: 20rem) {
		${CampaignReading} ${CampaignCopy} { display: grid; gap: 8px; --campaign-title-size: clamp(26px, 9cqw, 36px); }
		${CampaignReading} ${CampaignCopy} ${PlanLabel}, ${CampaignReading} ${CampaignCopy} ${CampaignLede}, ${CampaignReading} ${CampaignCopy} ${CampaignCta} { margin: 0; }
		${CampaignReading} ${CampaignCopy} ${CampaignLede} { font-size: 13px; line-height: 1.4; }
		${CampaignReading} ${CampaignCopy} ${PlanLabel} { font-size: 12px; line-height: 1.3; }
		${CampaignReading} ${CampaignCopy} ${CampaignCta} { min-height: 44px; justify-self: start; padding-block: 8px; }
	}
	@container campaign-space (max-height: 15rem) {
		${CampaignReading} ${CampaignCopy} { gap: 6px; --campaign-title-size: clamp(24px, 8cqw, 32px); }
		${CampaignReading} ${CampaignCopy} ${PlanLabel} { display: none; }
		${CampaignReading} ${CampaignCopy}[data-edition="newspaper"] { border: 0; padding: 0; }
	}
	@container campaign-space (max-height: 11rem) {
		${CampaignReading} ${CampaignCopy} ${CampaignLede} { display: none; }
		${CampaignReading} ${CampaignCopy} { gap: 8px; --campaign-title-size: clamp(24px, min(8cqw, calc((100cqh - 52px) / 2)), 32px); }
	}
	${CampaignReading}[data-space-strip] ${CampaignCopy} {
		display: grid;
		grid-template-columns: minmax(0, 1fr) auto;
		align-items: center;
		gap: 8px;
		--campaign-title-size: 14px;
	}
	${CampaignReading}[data-space-strip] ${CampaignCta} { font-size: 12px; gap: 8px; padding-inline: 12px; }
`;
